/**
 * Mock content for the Campaigns section.
 *
 * TODO(design): every row and template here is placeholder data. A developer
 * needs to replace these with the real endpoints — see DEVELOPER_TASKS.md.
 */

export type CampaignStatus = "Draft" | "Sent";

/**
 * What a field shows where the campaign has no answer for it yet.
 *
 * A draft has not been sent, so it has no count and no date. Those used to
 * carry numbers anyway — one draft claimed 1,278 recipients — which read as
 * fact rather than as placeholder.
 */
export const NOT_SENT = "—";

export interface CampaignRow {
  /** The shared Table requires both an id and a key on every row. */
  id: number;
  key: string;
  subject: string;
  audience: string;
  sent: number;
  at: string;
  status: CampaignStatus;
  /**
   * Which of the templates the letter behind this row was built from, where it
   * was built from one. The preview draws that template's blocks instead of a
   * blank letter, so the drawer shows something a reader would recognise.
   *
   * TODO(design): mock only. A real campaign carries its own letter, and a
   * developer replaces this with whatever the API returns for it.
   */
  template?: string;
}

const BASE_ROWS: CampaignRow[] = [
  // One campaign per template, so every example letter can be opened from the
  // list and seen in the preview. The rest of the rows below carry no template
  // and fall back to a blank letter.
  {
    id: 101,
    key: "101",
    subject: "Autumn sale is live",
    audience: "All users",
    sent: 2140,
    at: "2026-09-02 09:30",
    status: "Sent",
    template: "promo",
  },
  {
    id: 102,
    key: "102",
    subject: "Welcome to the team",
    audience: "New users",
    sent: 0,
    at: "",
    status: "Draft",
    template: "welcome",
  },
  {
    id: 103,
    key: "103",
    subject: "September in review",
    audience: "All users",
    sent: 1875,
    at: "2026-09-01 08:00",
    status: "Sent",
    template: "digest",
  },
  {
    id: 1,
    key: "1",
    subject: "What's new this month",
    audience: "All users",
    sent: 0,
    at: "",
    status: "Draft",
  },
  {
    id: 2,
    key: "2",
    subject: "Welcome to our community",
    audience: "New users",
    sent: 0,
    at: "",
    status: "Draft",
  },
  {
    id: 3,
    key: "3",
    subject: "Your weekly update",
    audience: "Premium users",
    sent: 169,
    at: "2026-08-21 09:00",
    status: "Sent",
  },
  {
    id: 4,
    key: "4",
    subject: "Product spotlight: automations",
    audience: "All users",
    sent: 1240,
    at: "2026-08-19 14:30",
    status: "Sent",
  },
  {
    id: 5,
    key: "5",
    subject: "We miss you",
    audience: "Inactive users",
    sent: 412,
    at: "2026-08-18 11:05",
    status: "Sent",
  },
  {
    id: 6,
    key: "6",
    subject: "Autumn release notes",
    audience: "All users",
    sent: 0,
    at: "",
    status: "Draft",
  },
  {
    id: 7,
    key: "7",
    subject: "Invite your teammates",
    audience: "Admins",
    sent: 64,
    at: "2026-08-15 08:20",
    status: "Sent",
  },
  {
    id: 8,
    key: "8",
    subject: "Your subscription renews soon",
    audience: "Premium users",
    sent: 0,
    at: "2026-08-14 12:00",
    status: "Sent",
  },
  {
    id: 9,
    key: "9",
    subject: "Security tips for your account",
    audience: "All users",
    sent: 1301,
    at: "2026-08-12 10:00",
    status: "Sent",
  },
  {
    id: 10,
    key: "10",
    subject: "Tell us what you think",
    audience: "Active users",
    sent: 733,
    at: "2026-08-10 13:15",
    status: "Sent",
  },
  {
    id: 11,
    key: "11",
    subject: "Holiday schedule",
    audience: "All users",
    sent: 0,
    at: "",
    status: "Draft",
  },
  {
    id: 12,
    key: "12",
    subject: "New integrations are live",
    audience: "Premium users",
    sent: 158,
    at: "2026-08-05 15:25",
    status: "Sent",
  },
];

/**
 * Twelve pages of rows at the default page size, so the pager can be seen with
 * real paging rather than a single page. The twelve above are written out; the
 * rest cycle them with their own ids and a subject that says which pass it is.
 *
 * TODO(design): all of it is placeholder — see the note at the top of the file.
 */
export const CAMPAIGN_ROWS: CampaignRow[] = Array.from(
  { length: 120 },
  (_, index) => {
    const base = BASE_ROWS[index % BASE_ROWS.length];
    const pass = Math.floor(index / BASE_ROWS.length);

    return {
      ...base,
      id: index + 1,
      key: String(index + 1),
      subject: pass === 0 ? base.subject : `${base.subject} (${pass + 1})`,
      // Only a campaign that went out has a count to grow.
      sent: base.status === "Sent" ? base.sent + pass * 37 : 0,
    };
  },
);

export interface EmailTemplate {
  id: string;
  name: string;
  /** Block types the template lays out, in order. Rendered by the builder. */
  blocks: string[];
}

export const EMAIL_TEMPLATES: EmailTemplate[] = [
  // The five structural ones, named for the shape rather than the occasion:
  // what a template offers is an arrangement, and the words in it are a
  // starting point the user replaces. Their blocks are in blocks.ts.
  {
    id: "bands",
    name: "Full-width bands",
    blocks: [
      "image",
      "divider",
      "header",
      "text",
      "button",
      "image",
      "header",
      "text",
      "button",
      "social",
      "text",
    ],
  },
  {
    id: "cards",
    name: "Card stack",
    blocks: [
      "image",
      "header",
      "text",
      "button",
      "image",
      "header",
      "text",
      "button",
      "social",
      "text",
    ],
  },
  {
    id: "listing",
    name: "Listing",
    blocks: [
      "image",
      "header",
      "text",
      "imagetext",
      "button",
      "spacer",
      "imagetext",
      "button",
      "text",
      "button",
      "social",
      "text",
    ],
  },
  {
    id: "story",
    name: "One story",
    blocks: ["image", "image", "title", "text", "button", "social", "text"],
  },
  {
    id: "plain",
    name: "Plain letter",
    blocks: [
      "image",
      "text",
      "divider",
      "image",
      "title",
      "text",
      "button",
      "divider",
      "text",
      "social",
      "text",
    ],
  },
  {
    id: "promo",
    name: "Sale announcement",
    blocks: [
      "image",
      "header",
      "image",
      "text",
      "button",
      "divider",
      "text",
      "social",
    ],
  },
  {
    id: "welcome",
    name: "Welcome letter",
    blocks: [
      "image",
      "header",
      "text",
      "imagetext",
      "imagetext",
      "button",
      "text",
      "social",
    ],
  },
  {
    id: "digest",
    name: "Monthly newsletter",
    blocks: [
      "image",
      "header",
      "text",
      "divider",
      "title",
      "imagetext",
      "button",
      "divider",
      "title",
      "text",
      "text",
      "social",
    ],
  },
];

/** Who a campaign can go to. */
export interface Audience {
  id: string;
  name: string;
  /** How many people are in it.
   * TODO(design): needs the real list sizes from the audience endpoint. */
  recipients: number;
}

export const AUDIENCES: Audience[] = [
  { id: "all", name: "All subscribers", recipients: 12480 },
  { id: "newsletter", name: "Monthly newsletter", recipients: 8130 },
  { id: "product", name: "Product updates", recipients: 4275 },
  { id: "trial", name: "Trial users", recipients: 1265 },
];

/**
 * What a campaign is sent as before anyone changes it.
 *
 * TODO(design): needs the account's own sender identity — the organisation's
 * name and the address replies go to — rather than these two strings. Both
 * fields stay editable on the send screen either way.
 */
export const DEFAULT_SENDER = {
  name: "Company Name",
  replyTo: "hello@company.com",
};
